import { trackMatomoEvent } from './matomoTracking';
import { trackGa4Event } from './ga4Tracking';
import {
  AGGREGATE_COUNCIL_SLUG,
  COUNCILS,
  DEFAULT_COUNCIL_SLUG,
  getCouncilConfig,
  isAggregateCouncilScope,
} from './councils';

export type CityScopeJourney =
  | 'discovery'
  | 'planning_research'
  | 'reports'
  | 'community'
  | 'content'
  | 'account'
  | 'engagement'
  | 'navigation';

export interface CityScopeAnalyticsEvent {
  category: string;
  action: string;
  name?: string;
  value?: number;
  metadata?: Record<string, unknown>;
}

export interface CityScopeRouteMilestone {
  journey: CityScopeJourney;
  pageType: string;
  milestone: string;
  entityType?: string;
  entityId?: string;
  entityLabel?: string;
  sourceCouncil?: string;
}

const APPLICATION_REF_PATTERN = /\b\d{2}\/\d{4,6}\/[A-Z0-9-]+\b/i;
const DOCUMENT_PATTERN = /\.(?:pdf|doc|docx|xls|xlsx|csv|zip|jpg|jpeg|png)(?:$|[?#])/i;
const INTERNAL_HOSTS = new Set(['www.city-scope.co.uk', 'city-scope.co.uk']);
const SOURCE_COUNCIL_DIMENSION_ID =
  process.env.NEXT_PUBLIC_MATOMO_SOURCE_COUNCIL_DIMENSION_ID ||
  process.env.NEXT_PUBLIC_MATOMO_COUNCIL_DIMENSION_ID ||
  '1';

function cleanLabel(value?: string | null): string | undefined {
  const label = (value || '').replace(/\s+/g, ' ').trim();
  return label ? label.slice(0, 180) : undefined;
}

function parseUrl(value: string, baseOrigin = 'https://www.city-scope.co.uk'): URL | null {
  try {
    return new URL(value, baseOrigin);
  } catch {
    return null;
  }
}

function safeDecodePathSegment(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function pathSegments(path: string): string[] {
  return path.split('/').filter(Boolean).map(safeDecodePathSegment);
}

function cleanCouncilSlug(value?: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const raw = value.trim().toLowerCase();
  if (!raw) return undefined;
  if (isAggregateCouncilScope(raw)) return AGGREGATE_COUNCIL_SLUG;

  const council = getCouncilConfig(raw);
  if (council) return council.slug;

  return /^[a-z][a-z0-9_-]{1,39}$/.test(raw) ? raw : undefined;
}

function councilFromPathSegment(value?: string) {
  const slug = cleanCouncilSlug(value);
  if (!slug) return null;
  if (slug === AGGREGATE_COUNCIL_SLUG) {
    return { slug: AGGREGATE_COUNCIL_SLUG, displayName: 'Scotland' };
  }

  const council = getCouncilConfig(slug);
  return council ? { slug: council.slug, displayName: council.displayName } : null;
}

function councilFromPortalHost(hostname: string) {
  const normalizedHost = String(hostname || '').toLowerCase();
  return Object.values(COUNCILS).find((council) => {
    try {
      return new URL(council.portalBaseUrl).hostname.toLowerCase() === normalizedHost;
    } catch {
      return false;
    }
  });
}

export function getSourceCouncilForAnalyticsUrl(
  rawUrl: string,
  metadata: Record<string, unknown> = {},
): string | undefined {
  const metadataCouncil =
    cleanCouncilSlug(metadata.source_council) ||
    cleanCouncilSlug(metadata.council_slug) ||
    cleanCouncilSlug(metadata.council);
  if (metadataCouncil) return metadataCouncil;

  const parsed = parseUrl(rawUrl);
  const pathname = parsed?.pathname || rawUrl.split('?')[0] || '/';
  const path = pathname.replace(/\/+$/, '') || '/';
  const segments = pathSegments(path);
  const reference = extractApplicationReference(path);

  if (segments[0] === 'planning-application') {
    return DEFAULT_COUNCIL_SLUG;
  }

  if (segments[0] === 'planning-updates') {
    if (segments.length === 1) return AGGREGATE_COUNCIL_SLUG;
    const council = councilFromPathSegment(segments[1]);
    if (council) return council.slug;
    if (reference) return DEFAULT_COUNCIL_SLUG;
  }

  if (segments[0] === 'planning-map') {
    if (segments.length === 1) return AGGREGATE_COUNCIL_SLUG;
    return councilFromPathSegment(segments[1])?.slug;
  }

  return undefined;
}

export function buildCouncilCustomDimensionsForUrl(
  rawUrl: string,
  metadata: Record<string, unknown> = {},
): Record<string, string> | undefined {
  const sourceCouncil = getSourceCouncilForAnalyticsUrl(rawUrl, metadata);
  return sourceCouncil ? { [SOURCE_COUNCIL_DIMENSION_ID]: sourceCouncil } : undefined;
}

function buildCouncilCustomDimensionsFromMetadata(
  metadata?: Record<string, unknown>,
): Record<string, string> | undefined {
  const sourceCouncil = metadata
    ? getSourceCouncilForAnalyticsUrl('', metadata)
    : undefined;
  return sourceCouncil ? { [SOURCE_COUNCIL_DIMENSION_ID]: sourceCouncil } : undefined;
}

export function extractApplicationReference(value: string): string | undefined {
  try {
    const decoded = decodeURIComponent(value);
    const match = decoded.match(APPLICATION_REF_PATTERN);
    return match?.[0]?.toUpperCase();
  } catch {
    const match = value.match(APPLICATION_REF_PATTERN);
    return match?.[0]?.toUpperCase();
  }
}

function extractSlug(pathname: string, prefix: string): string | undefined {
  const normalizedPrefix = prefix.endsWith('/') ? prefix : `${prefix}/`;
  if (!pathname.startsWith(normalizedPrefix)) return undefined;
  return cleanLabel(pathname.slice(normalizedPrefix.length).split('/')[0]);
}

export function classifyCityScopeRoute(rawUrl: string): CityScopeRouteMilestone {
  const parsed = parseUrl(rawUrl);
  const pathname = parsed?.pathname || rawUrl.split('?')[0] || '/';
  const path = pathname.replace(/\/+$/, '') || '/';
  const reference = extractApplicationReference(path);
  const segments = pathSegments(path);
  const sourceCouncil = getSourceCouncilForAnalyticsUrl(path);

  if (path === '/') {
    return {
      journey: 'discovery',
      pageType: 'homepage',
      milestone: 'homepage_viewed',
    };
  }

  if (path === '/planning-map') {
    return {
      journey: 'planning_research',
      pageType: 'planning_map',
      milestone: 'planning_map_viewed',
      entityType: 'source_council',
      entityId: AGGREGATE_COUNCIL_SLUG,
      entityLabel: 'Scotland',
      sourceCouncil: AGGREGATE_COUNCIL_SLUG,
    };
  }

  if (segments[0] === 'planning-map' && segments.length === 2) {
    const council = councilFromPathSegment(segments[1]);
    if (council) {
      return {
        journey: 'planning_research',
        pageType: 'planning_map',
        milestone: 'planning_map_viewed',
        entityType: 'source_council',
        entityId: council.slug,
        entityLabel: council.displayName,
        sourceCouncil: council.slug,
      };
    }
  }

  if (path === '/planning-updates' || path === '/planning-updates-html') {
    return {
      journey: 'planning_research',
      pageType: 'planning_updates',
      milestone: 'planning_updates_viewed',
      entityType: 'source_council',
      entityId: AGGREGATE_COUNCIL_SLUG,
      entityLabel: 'Scotland',
      sourceCouncil: AGGREGATE_COUNCIL_SLUG,
    };
  }

  if (segments[0] === 'planning-updates' && segments.length === 2) {
    const council = councilFromPathSegment(segments[1]);
    if (council) {
      return {
        journey: 'planning_research',
        pageType: 'planning_updates',
        milestone: 'planning_updates_viewed',
        entityType: 'source_council',
        entityId: council.slug,
        entityLabel: council.displayName,
        sourceCouncil: council.slug,
      };
    }
  }

  if (path.startsWith('/planning-updates/daily/')) {
    return {
      journey: 'planning_research',
      pageType: 'daily_digest',
      milestone: 'daily_digest_viewed',
      entityType: 'date',
      entityId: cleanLabel(path.split('/').pop()),
    };
  }

  if (path.startsWith('/planning-application/') || (path.startsWith('/planning-updates/') && reference)) {
    return {
      journey: 'planning_research',
      pageType: 'application_detail',
      milestone: 'application_opened',
      entityType: 'planning_application',
      entityId: reference,
      entityLabel: reference,
      sourceCouncil,
    };
  }

  if (path === '/planning-analytics') {
    return {
      journey: 'planning_research',
      pageType: 'planning_analytics',
      milestone: 'analytics_dashboard_viewed',
    };
  }

  if (path === '/planning-projects') {
    return {
      journey: 'planning_research',
      pageType: 'planning_projects',
      milestone: 'planning_projects_viewed',
    };
  }

  if (path.startsWith('/planning-archive')) {
    return {
      journey: 'planning_research',
      pageType: 'planning_archive',
      milestone: 'planning_archive_viewed',
    };
  }

  if (path === '/weekly-planning-reports' || path === '/short-lets-report') {
    return {
      journey: 'reports',
      pageType: 'report_offer',
      milestone: 'report_offer_viewed',
      entityType: 'report_type',
      entityId: path.slice(1),
    };
  }

  if (path === '/reports/signup') {
    return {
      journey: 'reports',
      pageType: 'report_signup',
      milestone: 'report_signup_form_viewed',
    };
  }

  if (path === '/reports/sample') {
    return {
      journey: 'reports',
      pageType: 'sample_report',
      milestone: 'sample_report_viewed',
    };
  }

  if (path === '/reports/unsubscribe') {
    return {
      journey: 'reports',
      pageType: 'report_unsubscribe',
      milestone: 'report_unsubscribe_viewed',
    };
  }

  if (path === '/blog') {
    return {
      journey: 'content',
      pageType: 'blog_index',
      milestone: 'article_index_viewed',
    };
  }

  if (path.startsWith('/blog/')) {
    const slug = extractSlug(path, '/blog');
    return {
      journey: 'content',
      pageType: 'article',
      milestone: 'article_viewed',
      entityType: 'article',
      entityId: slug,
      entityLabel: slug,
    };
  }

  if (path === '/communities') {
    return {
      journey: 'community',
      pageType: 'communities_index',
      milestone: 'communities_index_viewed',
    };
  }

  if (path.startsWith('/communities/') && path.endsWith('/applications')) {
    const slug = extractSlug(path, '/communities');
    return {
      journey: 'community',
      pageType: 'community_applications',
      milestone: 'community_applications_viewed',
      entityType: 'community',
      entityId: slug,
      entityLabel: slug,
    };
  }

  if (path.startsWith('/communities/')) {
    const slug = extractSlug(path, '/communities');
    return {
      journey: 'community',
      pageType: 'community',
      milestone: 'community_viewed',
      entityType: 'community',
      entityId: slug,
      entityLabel: slug,
    };
  }

  if (path.startsWith('/account') || path.startsWith('/accounts')) {
    return {
      journey: 'account',
      pageType: 'account_area',
      milestone: 'account_area_viewed',
    };
  }

  if (path === '/auth' || path.startsWith('/auth/')) {
    const mode = cleanLabel(parsed?.searchParams.get('mode'));
    return {
      journey: 'account',
      pageType: 'auth',
      milestone: 'auth_viewed',
      entityType: mode ? 'auth_mode' : undefined,
      entityId: mode,
    };
  }

  if (path === '/active-travel' || path.startsWith('/roadworks') || path.startsWith('/tram')) {
    return {
      journey: 'discovery',
      pageType: 'civic_map',
      milestone: 'civic_map_viewed',
      entityType: 'map_type',
      entityId: path.slice(1) || 'unknown',
    };
  }

  return {
    journey: 'navigation',
    pageType: 'other',
    milestone: 'cityscope_page_viewed',
    entityType: 'path',
    entityId: path,
  };
}

export function classifyCityScopeHref(
  href: string,
  label?: string,
  baseOrigin = 'https://www.city-scope.co.uk',
): CityScopeAnalyticsEvent | null {
  const parsed = parseUrl(href, baseOrigin);
  if (!parsed) return null;

  const cleanName = cleanLabel(label) || parsed.pathname;
  const hostname = parsed.hostname.toLowerCase();
  const reference = extractApplicationReference(parsed.pathname + parsed.search);
  const isCityScopeHost = INTERNAL_HOSTS.has(hostname) || hostname === new URL(baseOrigin).hostname;
  const looksLikeDocument = DOCUMENT_PATTERN.test(parsed.pathname + parsed.search);
  const pathAndSearch = parsed.pathname + parsed.search;
  const looksLikeApplicationForm =
    /application[_-]?form|\/forms\/|\/form(?:$|[/?#])|activeTab=makeComment/i.test(pathAndSearch) ||
    /application form|comment|form/i.test(cleanName);
  const looksLikeCouncilDocuments =
    looksLikeDocument || /activeTab=documents/i.test(pathAndSearch) || /documents?/i.test(cleanName);

  const portalCouncil = councilFromPortalHost(hostname);
  if (portalCouncil) {
    const action = looksLikeApplicationForm
      ? 'application_form_opened'
      : looksLikeCouncilDocuments
        ? 'council_documents_opened'
        : 'council_record_opened';
    return {
      category: 'journey:planning_research',
      action,
      name: reference || cleanName,
      metadata: {
        hostname,
        reference,
        destination: `${portalCouncil.slug}_idox`,
        source_council: portalCouncil.slug,
      },
    };
  }

  if (!isCityScopeHost) {
    return {
      category: looksLikeDocument ? 'journey:planning_research' : 'outbound',
      action: looksLikeDocument ? 'external_document_opened' : 'outbound_clicked',
      name: cleanName,
      metadata: { hostname, path: parsed.pathname },
    };
  }

  if (looksLikeApplicationForm || /\/form(?:$|[/?#])/i.test(parsed.pathname)) {
    return {
      category: 'journey:planning_research',
      action: 'application_form_opened',
      name: reference || cleanName,
      metadata: { reference, path: parsed.pathname },
    };
  }

  if (looksLikeDocument) {
    return {
      category: 'journey:planning_research',
      action: 'document_opened',
      name: cleanName,
      metadata: { path: parsed.pathname },
    };
  }

  const route = classifyCityScopeRoute(`${parsed.pathname}${parsed.search}${parsed.hash}`);
  return {
    category: `journey:${route.journey}`,
    action: route.milestone,
    name: route.entityLabel || cleanName,
    metadata: {
      page_type: route.pageType,
      entity_type: route.entityType,
      entity_id: route.entityId,
      source_council: route.sourceCouncil,
      interaction: 'link_click',
    },
  };
}

export function trackCityScopeEvent(event: CityScopeAnalyticsEvent): void {
  const customDimensions = buildCouncilCustomDimensionsFromMetadata(event.metadata);

  trackMatomoEvent({
    category: event.category,
    action: event.action,
    name: event.name,
    value: event.value,
    customDimensions,
    metadata: event.metadata,
  });
  trackGa4Event(event);
}

export function trackRouteJourneyMilestone(
  url: string,
  metadata: Record<string, unknown> = {},
): void {
  const route = classifyCityScopeRoute(url);
  const sourceCouncil = route.sourceCouncil || getSourceCouncilForAnalyticsUrl(url, metadata);
  trackCityScopeEvent({
    category: `journey:${route.journey}`,
    action: route.milestone,
    name: route.entityLabel || route.entityId || route.pageType,
    metadata: {
      ...metadata,
      page_type: route.pageType,
      entity_type: route.entityType,
      entity_id: route.entityId,
      source_council: sourceCouncil,
      source: 'route',
    },
  });
}

